import { useGlobalSearchParams } from "expo-router";

import { ClinicRequestStatus } from "@/api/generated/models";
import { useGetClinicStatusForPatient } from "@/api/queries/clinicStatus";
import { Button, ChatNote, Logo, Theme } from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import type { CuraiRedirectProps } from "@/conversation/AssistantMessage/ClinicRequest/CuraiRedirect";
import VisitStatusCard from "@/conversation/AssistantMessage/ClinicRequest/VisitStatusCard";
import { useCurrentConversationId } from "@/hooks/conversation";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";

type AccessClinicRequestProps = CuraiRedirectProps & {
  requestStatus: ClinicRequestStatus;
};

export default function AccessClinicRequest({
  requestStatus,
  clinicConversationId,
  clinicRequestId,
  conversationId: eventConversationId,
  messageId,
  feedback,
  feedbackComment,
  maybeCopy,
  isLatest,
  content,
}: AccessClinicRequestProps) {
  const { clinic_request_id: urlClinicRequestId } = useGlobalSearchParams<{
    clinic_request_id?: string;
  }>();
  const urlConversationId = useCurrentConversationId();
  const conversationId = eventConversationId ?? urlConversationId;
  const router = usePropagatedRouter();
  const { value: visitsDisabled, loading: isVisitsFlagLoading } =
    useFeatureFlag("visits-disabled", false);
  const { data: clinicStatus, isLoading: isClinicStatusLoading } =
    useGetClinicStatusForPatient({
      enabled:
        requestStatus === ClinicRequestStatus.created && !clinicConversationId,
    });
  if (isVisitsFlagLoading || isClinicStatusLoading)
    return <ChatNote.Skeleton />;
  if (
    !visitsDisabled &&
    requestStatus === ClinicRequestStatus.created &&
    !clinicConversationId &&
    urlClinicRequestId === clinicRequestId
  ) {
    return null;
  }

  let fallback;
  if (visitsDisabled) {
    fallback = (
      <Theme name="clinic">
        <ChatNote.Active
          Icon={Logo.Only}
          title="Clinic currently unavailable"
          subtitle="Our clinic isn't accepting new visits right now. Please check back soon."
          button={
            <Button mode="primary" disabled={true}>
              Unavailable
            </Button>
          }
        />
      </Theme>
    );
  } else if (
    requestStatus === ClinicRequestStatus.created &&
    !clinicConversationId
  ) {
    if (clinicStatus?.hasOngoingClinicVisit) {
      fallback = (
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="Enrollment paused"
            subtitle="You already have an active visit. Finish that visit before continuing your ACCESS enrollment."
          />
        </Theme>
      );
    } else {
      fallback = (
        <Theme name="clinic">
          <ChatNote.Active
            Icon={Logo.Only}
            title="Continue enrollment"
            subtitle="Continue enrolling in your care program."
            button={
              <Button
                mode="primary"
                onPress={() =>
                  router.navigate(
                    `/conversation/${conversationId}?clinic_request_id=${clinicRequestId}`,
                  )
                }
                dataTrackingId="continue-access-enrollment"
              >
                Continue enrollment
              </Button>
            }
          />
        </Theme>
      );
    }
  } else {
    fallback = (
      <ChatNote.Active
        Icon={Logo.Only}
        title="Enrollment request closed"
        subtitle="This request can no longer be resumed."
      />
    );
  }

  return (
    <VisitStatusCard
      content={content}
      conversationId={conversationId}
      clinicRequestId={clinicRequestId}
      clinicConversationId={clinicConversationId}
      messageId={messageId}
      feedback={feedback}
      feedbackComment={feedbackComment}
      maybeCopy={maybeCopy}
      isLatest={isLatest}
      fallback={fallback}
    />
  );
}
